import { supabase } from '@/lib/supabase';
import { logger } from '@/lib/logger';

// ─────────────────────────────────────────────────────────────
// shopPayment — paiement d'une commande de boutique via la MODALE FeexPay.
//
// Le tunnel est le même que celui de la page d'abonnement :
//   1. `prepare` → le serveur vérifie la commande, l'autorisation du commerce
//      et le moyen activé, puis renvoie la référence + les identifiants de la
//      modale (Shop ID / jeton) et le montant converti dans la devise de
//      l'opérateur choisi.
//   2. la modale FeexPay s'ouvre : c'est LÀ que le client saisit ses
//      informations et valide sur son téléphone. Rien n'est débité avant.
//   3. `verify` → le serveur interroge réellement FeexPay. Uniquement s'il
//      confirme l'encaissement, le webhook interne marque la commande payée,
//      crédite le commerce et débloque les produits digitaux.
// ─────────────────────────────────────────────────────────────

export interface ShopPaymentPrepareResult {
  reference: string;
  shopId: string;
  token: string;
  /** Montant demandé, dans la devise de la boutique. */
  amount: number;
  currency: string;
  /** Montant réellement débité par l'opérateur (XOF / XAF). */
  gatewayAmount: number;
  gatewayCurrency: string;
  commissionPercent: number;
  configured: boolean;
}

export interface ShopPaymentVerifyResult {
  status: 'paid' | 'failed' | 'pending';
  orderId: number;
  amount?: number;
  currency?: string;
  message?: string;
  gatewayReason?: string | null;
}

async function readFunctionError(error: unknown, fallback: string): Promise<string> {
  const ctx = (error as { context?: { json?: () => Promise<{ error?: string }> } }).context;
  if (ctx && typeof ctx.json === 'function') {
    try {
      const body = await ctx.json();
      if (body?.error) return String(body.error);
    } catch {
      /* corps illisible */
    }
  }
  return fallback;
}

async function invoke<T>(body: Record<string, unknown>, fallbackError: string): Promise<T> {
  const { data, error } = await supabase.functions.invoke('zifek-shop-payment', { body });
  if (error) throw new Error(await readFunctionError(error, fallbackError));
  return (data || {}) as T;
}

/** Prépare la modale de paiement d'une commande. */
export function prepareShopPayment(params: {
  orderId: number;
  amount: number;
  country: string;
  network: string;
  gatewayCurrency: string;
  method?: 'mobile_money' | 'card';
  phone?: string;
}): Promise<ShopPaymentPrepareResult> {
  return invoke<ShopPaymentPrepareResult>(
    {
      action: 'prepare',
      orderId: params.orderId,
      amount: params.amount,
      country: params.country,
      network: params.network,
      gatewayCurrency: params.gatewayCurrency,
      method: params.method || 'mobile_money',
      phone: params.phone || '',
      origin: typeof window !== 'undefined' ? window.location.origin : '',
      basePath: typeof __BASE_PATH__ !== 'undefined' ? __BASE_PATH__ : '',
    },
    "Le paiement en ligne n'est pas disponible pour cette commande.",
  );
}

/** Interroge l'opérateur pour savoir si la commande est réellement encaissée. */
export function verifyShopPayment(params: {
  orderId: number;
  reference: string;
  widgetStatus?: string;
  transactionReference?: string;
}): Promise<ShopPaymentVerifyResult> {
  return invoke<ShopPaymentVerifyResult>(
    {
      action: 'verify',
      orderId: params.orderId,
      reference: params.reference,
      widgetStatus: params.widgetStatus || '',
      transactionReference: params.transactionReference || '',
    },
    'Vérification du paiement impossible pour le moment.',
  );
}

const WIDGET_SUCCESS = new Set(['success', 'successful', 'paid', 'completed', 'complete', 'approved', 'ok', 'succeeded', '00', '0000']);
const WIDGET_FAILURE = new Set(['failed', 'failure', 'declined', 'cancelled', 'canceled', 'expired', 'rejected', 'error', 'refused', 'refuse', 'reversed']);

/**
 * Lit le résultat renvoyé par le SDK FeexPay (dont la forme n'est pas garantie).
 * Le statut n'est qu'un indice : la confirmation réelle vient toujours du
 * serveur, qui interroge l'opérateur.
 */
export function readWidgetOutcome(result: unknown): {
  success: boolean;
  failure: boolean;
  transactionReference: string;
} {
  const statuses: string[] = [];
  const refs: string[] = [];

  const walk = (value: unknown, depth: number) => {
    if (value == null || depth > 3) return;
    if (typeof value === 'string') return;
    if (Array.isArray(value)) {
      value.slice(0, 5).forEach((item) => walk(item, depth + 1));
      return;
    }
    if (typeof value !== 'object') return;
    Object.entries(value as Record<string, unknown>).forEach(([key, raw]) => {
      const k = key.toLowerCase();
      if (
        ['status', 'state', 'statut', 'transaction_status', 'payment_status'].includes(k)
        && (typeof raw === 'string' || typeof raw === 'number')
      ) {
        statuses.push(String(raw).trim().toLowerCase());
      }
      if (
        ['reference', 'transaction_reference', 'transactionid', 'transaction_id', 'txn_id', 'payment_id', 'pay_id', 'referenceid'].includes(k)
        && (typeof raw === 'string' || typeof raw === 'number')
      ) {
        const ref = String(raw).trim();
        if (ref && !/^SHOP-/i.test(ref)) refs.push(ref);
      }
      walk(raw, depth + 1);
    });
  };

  walk(result, 0);
  const success = statuses.some((s) => WIDGET_SUCCESS.has(s));
  const failure = !success && statuses.some((s) => WIDGET_FAILURE.has(s));
  if (success) logger.info('paiement', 'Modale FeexPay — paiement validé', { reference: refs[0] || null });
  return { success, failure, transactionReference: refs[0] || '' };
}